import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { PrivateRoute, AdminRoute } from './components/RouteGuards';
import Layout from './components/Layout';
import ResponsiveStyles from './components/ResponsiveStyles';

/* Public pages */
import Login         from './pages/Login';
import Signup        from './pages/Signup';
import VerifyEmail   from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword  from './pages/ResetPassword';

/* User pages */
import Dashboard        from './pages/Dashboard';
import Earnings         from './pages/Earnings';
import Activity         from './pages/Activity';
import Withdraw         from './pages/Withdraw';
import PlatformAccounts from './pages/PlatformAccounts';
import Settings         from './pages/Settings';

/* Admin pages */
import AdminDashboard        from './pages/admin/AdminDashboard';
import AdminUsers            from './pages/admin/AdminUsers';
import AdminEarnings         from './pages/admin/AdminEarnings';
import AdminWithdrawals      from './pages/admin/AdminWithdrawals';
import AdminPlatformAccounts from './pages/admin/AdminPlatformAccounts';
import AdminActivity         from './pages/admin/AdminActivity';
import AdminReferrals        from './pages/admin/AdminReferrals';

/* ── Tawk.to Live Chat ───────────────────────────────────
   Replace TAWK_PROPERTY_ID and TAWK_WIDGET_ID with your
   own IDs from tawk.to → Administration → Chat Widget.
   Leave as-is for now — it simply won't load until you
   add your real IDs.
──────────────────────────────────────────────────────── */
const TAWK_PROPERTY_ID = '69dbadc76161b11c33210537';
const TAWK_WIDGET_ID   = '1jm11tlon';

function TawkChat() {
  useEffect(() => {
    if (TAWK_PROPERTY_ID === 'YOUR_PROPERTY_ID') return; /* Not configured yet */
    const s = document.createElement('script');
    s.async = true;
    s.src   = `https://embed.tawk.to/${TAWK_PROPERTY_ID}/${TAWK_WIDGET_ID}`;
    s.charset = 'UTF-8';
    s.setAttribute('crossorigin', '*');
    document.head.appendChild(s);
    return () => { document.head.removeChild(s); };
  }, []);
  return null;
}

export default function App() {
  return (
    <AuthProvider>
      <ResponsiveStyles />
      <TawkChat />
      <BrowserRouter>
        <Routes>
          {/* ── Public ─────────────────────────────────────── */}
          {/* "/" is served as static HTML by Vercel (public/landing.html) */}
          <Route path="/login"           element={<Login />} />
          <Route path="/signup"          element={<Signup />} />
          <Route path="/verify-email"    element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password"  element={<ResetPassword />} />

          {/* ── User Dashboard ─────────────────────────────── */}
          <Route path="/dashboard" element={<PrivateRoute><Layout><Dashboard /></Layout></PrivateRoute>} />
          <Route path="/dashboard/earnings"  element={<PrivateRoute><Layout><Earnings /></Layout></PrivateRoute>} />
          <Route path="/dashboard/activity"  element={<PrivateRoute><Layout><Activity /></Layout></PrivateRoute>} />
          <Route path="/dashboard/withdraw"  element={<PrivateRoute><Layout><Withdraw /></Layout></PrivateRoute>} />
          <Route path="/dashboard/platforms" element={<PrivateRoute><Layout><PlatformAccounts /></Layout></PrivateRoute>} />
          <Route path="/dashboard/settings"  element={<PrivateRoute><Layout><Settings /></Layout></PrivateRoute>} />

          {/* ── Admin Panel ────────────────────────────────── */}
          <Route path="/admin"                    element={<AdminRoute><Layout><AdminDashboard /></Layout></AdminRoute>} />
          <Route path="/admin/users"              element={<AdminRoute><Layout><AdminUsers /></Layout></AdminRoute>} />
          <Route path="/admin/earnings"           element={<AdminRoute><Layout><AdminEarnings /></Layout></AdminRoute>} />
          <Route path="/admin/withdrawals"        element={<AdminRoute><Layout><AdminWithdrawals /></Layout></AdminRoute>} />
          <Route path="/admin/platform-accounts"  element={<AdminRoute><Layout><AdminPlatformAccounts /></Layout></AdminRoute>} />
          <Route path="/admin/activity"           element={<AdminRoute><Layout><AdminActivity /></Layout></AdminRoute>} />
          <Route path="/admin/referrals"          element={<AdminRoute><Layout><AdminReferrals /></Layout></AdminRoute>} />

          {/* ── Fallback ───────────────────────────────────── */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
